<template>
    <div>

        <rkm-area
            title="Paciente"
            type="fieldset"
            no-margin-top
            v-if="paciente"
        >

            <rkm-row>
                <rkm-col>
                    <h4>
                        {{ paciente.nome }}
                        <strong v-if="paciente.nomeSocial && paciente.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                            ({{ paciente.nomeSocial }})
                        </strong>

                        <span
                            v-if="paciente.responsavelFamiliar"
                            class="ml-1"
                            title="Responsável familiar"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="crown" />
                        </span>

                        <span
                            v-if="isTea"
                            class="ml-1"
                            title="Pessoa com Transtorno do Espectro Autista - TEA"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="infinity" />
                        </span>

                        <span
                            v-if="isPcd"
                            class="ml-1"
                            title="Pessoa com Deficiência - PCD"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="wheelchair" />
                        </span>
                    </h4>
                </rkm-col>
            </rkm-row>

            <rkm-row v-if="paciente.acompanhamentosSaude && paciente.acompanhamentosSaude.length > 0">
                <rkm-col>
                    <div class="health-flags">
                        <span class="health-title">
                            Acompanhamentos:
                        </span>

                        <span
                            v-for="(acompanhamento, index) in paciente.acompanhamentosSaude"
                            :key="index"
                            :class="`health-badge acompanhamento-${acompanhamento.value}`"
                        >
                            {{ acompanhamento.text }}
                        </span>
                    </div>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <p>
                        <strong>Prontuário:</strong>
                        {{ paciente.prontuario }}
                    </p>
                    <p>
                        <strong>CPF:</strong>
                        <template v-if="paciente.cpf">
                            {{ paciente.cpf | formatCpf }}
                        </template>
                        <template v-else>
                            <i>Não informado</i>
                        </template>
                    </p>
                    <p>
                        <strong>CNS:</strong>
                        <template v-if="paciente.cns">
                            {{ paciente.cns | formatCns }}
                        </template>
                        <template v-else>
                            <i>Não informado</i>
                        </template>
                    </p>
                    <p>
                        <strong>RG:</strong>
                        {{ paciente.rg || '-' }}
                    </p>
                    <p>
                        <strong>Telefone:</strong>
                        <template v-if="paciente.telefoneNumero">
                            {{ paciente.telefoneNumero | formatPhoneOrCellPhone }}
                        </template>
                        <template v-else>
                            -
                        </template>
                        <rkm-button
                            v-if="verificarPermissao('update-cadastro-paciente')"
                            @click="$refs.ModalTelefone.open(paciente)"
                            class="ml-1"
                            color="primary"
                            title="Alterar telefone"
                            v-rkm-tooltip.hover
                            small
                        >
                            <rkm-icon icon="pen" />
                        </rkm-button>
                    </p>
                </rkm-col>

                <rkm-col cols="4">
                    <p>
                        <strong>Data de nascimento:</strong>
                        <template v-if="paciente.dataNascimento">
                            {{ paciente.dataNascimento | formatDate }}
                        </template>
                        <template v-else>
                            <i>Não informado</i>
                        </template>
                    </p>
                    <p>
                        <strong>Idade:</strong>
                        <template v-if="paciente.idade">
                            {{ paciente.idade.anos }} anos,
                            {{ paciente.idade.meses }} meses e
                            {{ paciente.idade.dias }} dias
                        </template>
                        <template v-else>
                            <i>Não informado</i>
                        </template>
                    </p>
                    <p>
                        <strong>Sexo:</strong>
                        <template v-if="paciente.sexo">
                            {{ paciente.sexo.descricao }}
                        </template>
                        <template v-else>
                            <i>Não informado</i>
                        </template>
                    </p>
                    <p>
                        <strong>Nome da mãe:</strong>
                        <template v-if="paciente.nomeMae">
                            {{ paciente.nomeMae }}
                        </template>
                        <template v-else>
                            <i>Desconhecido</i>
                        </template>
                    </p>
                </rkm-col>

                <rkm-col cols="4">
                    <p>
                        <strong>Classificação:</strong>
                        {{ classificacaoDescricao }}
                    </p>
                    <p>
                        <strong>Unidade de referência:</strong>
                        <template v-if="paciente.unidadeReferencia">
                            {{ paciente.unidadeReferencia }}
                        </template>
                        <template v-else>
                            <i>Não informado no cadastro do paciente</i>
                        </template>
                    </p>
                    <p>
                        <strong>Área e Microárea:</strong>
                        {{ paciente.area || 'Não Informada' }} / {{ paciente.microarea || 'Não Informada' }}
                    </p>
                    <p>
                        <strong>INE:</strong>
                        {{ paciente.ine || 'Não Informado' }}
                    </p>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <p>
                        <strong>Endereço:</strong>
                        {{ paciente.endereco || 'Não informado' }}
                    </p>
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-table
            :fields="fields"
            :items="items"
        >

            <template v-slot:cell(data)="row">
                {{ row.value | formatDate }}
            </template>

            <template v-slot:cell(dataExportacao)="{ item }">
                {{ item.dataExportacao }} - {{ item.profissionalExportacao }}
            </template>

            <template v-slot:cell(actions)="{ item }">

                <rkm-button
                    @click.stop="search(item)"
                    color="primary"
                    title="Visualizar"
                    small
                >
                    <rkm-icon icon="search" />
                </rkm-button>

                <rkm-button
                    @click="handleDestroy(item)"
                    color="danger"
                    :disabled="item.dataExportacao"
                    small
                    title="Excluir"
                >
                    <rkm-icon icon="trash" />
                </rkm-button>
            </template>
        </rkm-table>

        <ModalTelefone ref="ModalTelefone" />
    </div>
</template>

<script>

import { mapActions } from 'vuex'

// Mixins.
import { repository, error, printExameLaboratorial, permissao, stringCase } from '@/mixins'

// Filters.
import format from '@/filters/formats'

//Enuns.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Components.
import ModalTelefone from '@/components/ambulatorio/components/paciente/telefone/Modal'

export default {
    components: { ModalTelefone },
    computed: {
        classificacaoDescricao() {

            if (this.paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO) {
                return 'Temporário'
            }

            if (this.paciente.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'Outro Município'
            }

            return 'Munícipe'
        },
        isTea() {

            return this.paciente.deficienciasBaseIds.includes(this.enums.deficienciaBase.TEA)
                || this.paciente.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        isPcd() {

            return this.paciente.deficienciasBaseIds.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!this.paciente.deficienciaId && this.paciente.deficienciaId !== this.enums.deficiencia.AUTISMO)
        },
    },
    created() {

        this.repositories = {
            atendimentoIndividual: this.getConectaSusRepository('atencaoBasica.atendimentoIndividual')
        }

        this.load()
    },
    data: () => ({
        fields: [
            {
                key: 'data',
                label: 'Data',
                sortable: true
            },
            {
                key: 'turno',
                label: 'Turno',
                sortable: true
            },
            {
                key: 'localAtendimento',
                label: 'Local de atendimento',
                sortable: true
            },
            {
                key: 'profissional',
                label: 'Profissional',
                sortable: true
            },
            {
                key: 'dataExportacao',
                label: 'Exportação',
            },
            {
                key: 'actions',
                label: 'Ações',
                class: 'col-min'
            },
        ],
        repositories: {
            paciente: null,
        },
        items: [],
        paciente: null,
        enums: {
            preferenciaNome,
            classificacao,
            subclassificacao,
            deficienciaBase,
            deficiencia
        },
    }),
    filters: {
        ...format,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {

            if (this.id) {
                this.setLoading()

                this.repositories.atendimentoIndividual.getAtendimentos({ id : this.id })
                    .then(response => {

                        this.additionals = response.data.additionals
                        this.items = Object.assign(response.data.atendimentos)
                        this.paciente = Object.assign(response.data.cidadao)

                        this.$emit('paciente', response.data.cidadao)

                        this.loaded = true

                        this.$emit('loaded', {additionals: this.additionals, cidadao: this.paciente})
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        search(item) {

           this.$router.push({ name: 'atencao-basica-atendimento-individual-edit', params: { id: item.id, pacienteId: this.id }})
        },
        handleDestroy({ id }) {

            const destroy = () => this.repositories.atendimentoIndividual.destroy(id)
                .then(() => this.items.splice(this.items.findIndex(i => i.id === id), 1))

            this.showDeleteConfirmation(`Quando um atendimento individual é excluído, se houver um atendimento de
                procedimento vinculado a ele, o atendimento de procedimento também será excluído. Deseja realmente
                excluir o atendimento?`, destroy, this.handleError)
        },
    },
    mixins: [ repository, error, printExameLaboratorial, permissao, stringCase ],
    props: {
        id: { type: Number, default: null }
    },
    watch: {
        'paciente.telefoneAtualizado'(atualizado) {

            if (!atualizado) return

            this.setLoading()

            this.repositories.atendimentoIndividual.updateTelefone(this.camelObjectKeysToSnakeRecursive({
                pacienteId: this.id,
                numero: this.paciente.telefoneNumero,
                autorizaSms: this.paciente.telefoneAutorizaSms,
            }))
                .then(() => {

                    this.paciente.telefoneAtualizado = false
                    this.showSaveNotification()
                })
                .catch(error => {

                    this.handleError(error)
                    this.load()
                })
                .finally(() => this.unsetLoading())
        },
    },
}
</script>

<style scoped>
p {
    margin: 0px;
}

.health-flags {
    margin-top: 9px;
    padding-top: 2px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #e5e7eb;
}

.health-title {
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.health-badge {
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
}

.acompanhamento-idoso {
    color: #155724;
}

.acompanhamento-gestante {
    color: #ffb6c1;
}

.acompanhamento-hipertenso,
.acompanhamento-diabetico {
    color: #856404;
}

.acompanhamento-saude-mulher {
    color: #b34db2;
}

.acompanhamento-crescimento-desenvolvimento-infantil {
    color: #004085;
}
</style>